import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Caption1,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { Add16Regular, Subtract16Regular } from '@fluentui/react-icons'
import { EditableField } from '../../components/EditableField'
import { canonicalizeTier } from '../../components/QuadrantPicker'
import { bucketColors } from '../../theme'
import { bayerColors, surfaces } from '../../theme'
import type { Demand } from '../../types/demand'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  section: {
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    padding: tokens.spacingVerticalL,
  },
  title: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: bayerColors.navy,
    margin: 0,
    marginBottom: tokens.spacingVerticalM,
  },
  // ── Quadrant matrix ─────────────────────────────────────────────────────
  quadrantGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridTemplateRows: '1fr 1fr',
    gap: '2px',
    maxWidth: '520px',
    background: tokens.colorNeutralStroke2,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
  },
  quadrant: {
    background: tokens.colorNeutralBackground1,
    borderRadius: '0',
    borderTopWidth: '0',
    borderRightWidth: '0',
    borderBottomWidth: '0',
    borderLeftWidth: '0',
    borderTopStyle: 'none',
    borderRightStyle: 'none',
    borderBottomStyle: 'none',
    borderLeftStyle: 'none',
    cursor: 'pointer',
    padding: '12px',
    minHeight: '80px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
    textAlign: 'left',
    position: 'relative',
    transitionProperty: 'background, color',
    transitionDuration: '120ms',
  },
  /**
   * A quadrant that cannot be picked. It keeps its tint — the grid still has to
   * read as a legend, and the active cell still has to be findable — but drops
   * the cursor and the hover response, which were the whole of the lie: the
   * tile invited a click and then did nothing at all, no toast, no change.
   */
  quadrantInert: { cursor: 'default', pointerEvents: 'none' },
  // Resting tints — always color-coded so quadrants are identifiable at a glance.
  quadrantMajor: { background: '#f5edff', ':hover': { background: '#ecdcff' } },
  quadrantHigh: { background: '#fde7e9', ':hover': { background: '#fbd0d5' } },
  quadrantQuick: { background: '#e9f8ef', ':hover': { background: '#d0f2dc' } },
  quadrantParking: { background: '#fdf0d2', ':hover': { background: '#fbe4a8' } },
  quadrantMajorActive: {
    background: '#7c3aed',
    color: '#fff',
    boxShadow: '0 0 0 3px #7c3aed, inset 0 0 0 2px rgba(255,255,255,0.4)',
    ':hover': { background: '#6d28d9' },
  },
  quadrantHighActive: {
    background: '#dc2626',
    color: '#fff',
    boxShadow: '0 0 0 3px #dc2626, inset 0 0 0 2px rgba(255,255,255,0.4)',
    ':hover': { background: '#b91c1c' },
  },
  quadrantQuickActive: {
    background: '#16a34a',
    color: '#fff',
    boxShadow: '0 0 0 3px #16a34a, inset 0 0 0 2px rgba(255,255,255,0.4)',
    ':hover': { background: '#15803d' },
  },
  quadrantParkingActive: {
    background: '#ca8a04',
    color: '#fff',
    boxShadow: '0 0 0 3px #ca8a04, inset 0 0 0 2px rgba(255,255,255,0.4)',
    ':hover': { background: '#a16207' },
  },
  quadrantTitle: {
    fontSize: '14px',
    fontWeight: 700,
  },
  quadrantSummary: {
    marginTop: '8px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  derivedNote: {
    fontStyle: 'italic',
  },
  age: {
    color: tokens.colorNeutralForeground3,
  },
  // Loud enough to notice on a demand coming back out of Parking Lot, which
  // is the case #953 was worried about — without having deleted the value.
  stale: {
    color: bucketColors.highPriority,
    fontWeight: tokens.fontWeightSemibold,
  },
  kvRow: {
    display: 'grid',
    gridTemplateColumns: '180px 1fr',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
  },
  kvKey: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground3,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
  },
  reasonHighlight: {
    animationName: {
      '0%': { backgroundColor: '#fff7e0' },
      '50%': { backgroundColor: '#fff1b8' },
      '100%': { backgroundColor: 'transparent' },
    },
    animationDuration: '2s',
    animationTimingFunction: 'ease-out',
    borderRadius: tokens.borderRadiusMedium,
    padding: '4px',
    marginLeft: '-4px',
    marginRight: '-4px',
  },
  // ── Detailed scoring ────────────────────────────────────────────────────
  criterion: {
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke3,
    ':last-child': { borderBottomStyle: 'none' },
  },
  criterionHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  scoreControl: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  scoreButton: {
    minWidth: '28px',
    width: '28px',
    height: '28px',
    padding: 0,
  },
  scoreValue: {
    minWidth: '52px',
    height: '28px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: '13px',
    borderRadius: tokens.borderRadiusMedium,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    color: tokens.colorNeutralForeground1,
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationFaster,
  },
  scoreValueUnset: {
    color: tokens.colorNeutralForeground3,
    fontWeight: 600,
    background: tokens.colorNeutralBackground2,
  },
  // ── Save bar ────────────────────────────────────────────────────────────
  saveBar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    background: surfaces.saveBarBg,
    borderRadius: tokens.borderRadiusLarge,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: surfaces.saveBarBorder,
    marginTop: '-4px',
  },
  saveBarText: {
    flex: 1,
    fontSize: tokens.fontSizeBase300,
    color: bayerColors.navyMid,
    fontWeight: tokens.fontWeightMedium,
  },
})

/**
 * The unsaved tier + score edits, held by whoever renders this tab.
 *
 * It lives outside the component because DemandDetail unmounts the tab body on
 * every tab switch, and a buffer inside would go with it.
 */
export interface PriorityDraft {
  tier: string | null
  scores: Record<string, number>
}

export const EMPTY_PRIORITY_DRAFT: PriorityDraft = { tier: null, scores: {} }

export function isPriorityDraftDirty(draft: PriorityDraft): boolean {
  return draft.tier !== null || Object.keys(draft.scores).length > 0
}

export interface PriorityTabProps {
  demand: Demand
  /** FA-scoped: false for a council viewing another FA's demand (read-only). */
  canEdit: boolean
  /** Single-field save — used by the rationale EditableFields only. */
  onSave: (path: string, value: string | string[] | number) => void
  /** One request for the whole tier+scores edit buffer (#1155): the old shape
   * fired up to five PATCHes per Save, each refetching the demand list, and
   * cleared the buffer before any of them answered — a rejected write lost
   * the user's edits behind a transient toast. Rejection = buffer kept. */
  onSaveMany: (patch: Record<string, string | number>) => Promise<unknown>
  /** Unsaved edits, owned by the parent so a tab switch cannot unmount them. */
  draft: PriorityDraft
  onDraftChange: (next: PriorityDraft) => void
}

interface CriterionDef {
  key: 'strategic_importance' | 'competitiveness' | 'readiness' | 'feasibility'
  label: string
  weight: number
}

const CRITERIA: CriterionDef[] = [
  { key: 'competitiveness', label: 'Impact', weight: 0.3 },
  { key: 'readiness', label: 'Effort', weight: 0.25 },
  { key: 'feasibility', label: 'Feasibility', weight: 0.25 },
  { key: 'strategic_importance', label: 'Strategic Alignment', weight: 0.2 },
]

const STEP = 0.1
const MIN = 0
/**
 * How old is this priority, in whole months? (#988 §7)
 *
 * #953 cleared the bucket when a demand was parked, so that resuming forced a
 * fresh look. That instinct was right — a demand parked for eight months
 * should not quietly return as "High Priority", because a stale priority still
 * reads as authoritative — but clearing destroyed the value to achieve it.
 * Showing the age gets the same attention without the data loss.
 */
function monthsSince(iso: string | null | undefined): number | null {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return null
  const months = (Date.now() - then) / (1000 * 60 * 60 * 24 * 30.44)
  return months >= 1 ? Math.floor(months) : 0
}

const STALE_AFTER_MONTHS = 6

const MAX = 1

interface QuadrantDef {
  key: 'major-unlocks' | 'high-priority' | 'quick-wins' | 'parking-lot'
  title: string
  hoverKey: 'quadrantMajor' | 'quadrantHigh' | 'quadrantQuick' | 'quadrantParking'
  activeKey:
    | 'quadrantMajorActive'
    | 'quadrantHighActive'
    | 'quadrantQuickActive'
    | 'quadrantParkingActive'
}

// Row-major: top-left, top-right, bottom-left, bottom-right.
// Captions intentionally absent — the tier name carries the meaning;
// we don't surface benefit/effort axes anywhere in the UI.
const QUADRANTS: QuadrantDef[] = [
  {
    key: 'major-unlocks',
    title: 'Major Unlocks',
    hoverKey: 'quadrantMajor',
    activeKey: 'quadrantMajorActive',
  },
  {
    key: 'high-priority',
    title: 'High Priority',
    hoverKey: 'quadrantHigh',
    activeKey: 'quadrantHighActive',
  },
  {
    key: 'quick-wins',
    title: 'Quick Wins',
    hoverKey: 'quadrantQuick',
    activeKey: 'quadrantQuickActive',
  },
  {
    key: 'parking-lot',
    title: 'Parking Lot',
    hoverKey: 'quadrantParking',
    activeKey: 'quadrantParkingActive',
  },
]

// Round to 1 decimal to avoid floating-point drift (0.1 + 0.1 + 0.1 = 0.30000…)
function roundStep(n: number): number {
  return Math.round(n * 10) / 10
}

// White → Bayer blue gradient. Hue locked at 212° (the Bayer navy
// family); saturation + lightness drive the visual progression so a
// score chip stays in the brand ramp instead of going red/green.
// Lightness held ≥65% so dark foreground text stays legible across
// the full 0.0-1.0 range.
function gradientBackground(score: number | null | undefined): string {
  if (score == null) return ''
  const clamped = Math.max(MIN, Math.min(MAX, score))
  const saturation = Math.round(clamped * 60) // 0 → 60
  const lightness = Math.round(95 - clamped * 30) // 95 → 65
  return `hsl(212, ${saturation}%, ${lightness}%)`
}

function formatScore(score: number | null | undefined): string {
  if (score == null) return '—'
  return roundStep(score).toFixed(1)
}

/**
 * Demand detail Priority tab.
 *
 * Two independent sections:
 *   1. Priority bucket — 2×2 color-coded matrix; one of Major Unlocks /
 *      High Priority / Quick Wins / Parking Lot. Canonical source of
 *      truth (priority_tier_override). Includes a free-text reason.
 *      NOT derived from the scores below.
 *   2. Detailed scoring — four FA criteria with 0.0–1.0 +/- controls
 *      and per-criterion reasons. Independent audit metadata.
 *
 * Both sections buffer their edits and commit together via the
 * Save/Cancel bar at the top.
 *
 * Parking Lot and Rejected are DERIVED (#988 §7) — consequences of the
 * demand's stage and closure, not buckets a council picks. They render as
 * state and are not selectable. There is no longer any stage coupling: the
 * old version fired a transition from here, a second untransacted request
 * after the PATCH had already committed, which could leave the bucket and
 * the stage disagreeing. To park a demand, move its stage.
 */
export function PriorityTab({
  demand,
  canEdit,
  onSave,
  onSaveMany,
  draft,
  onDraftChange,
}: PriorityTabProps) {
  const styles = useStyles()

  // The DERIVED bucket (#988 §7). Parking Lot and Rejected are consequences of
  // the stage and the closure, so the backend computes them; the stored column
  // only ever holds the three real priorities. Falls back to the raw column so
  // an older API response still renders.
  const activeTier = canonicalizeTier(demand.priority_bucket ?? demand.priority_tier_override)
  // Derived buckets are shown as state, not offered as a choice — picking one
  // would mean writing a value the backend refuses. Rejected is not a quadrant
  // key (the grid is 2x2), so it is read off the derived closure grouping
  // rather than given a fifth cell.
  const derivedTier = activeTier === 'parking-lot' || demand.closure_type === 'rejected'

  const scores = useMemo<Record<CriterionDef['key'], number | null>>(
    () => ({
      strategic_importance: demand.strategic_importance ?? null,
      competitiveness: demand.competitiveness ?? null,
      readiness: demand.readiness ?? null,
      feasibility: demand.feasibility ?? null,
    }),
    [demand],
  )

  // Buffered edits — committed via the Save bar, and owned by the PARENT.
  //
  // They used to be `useState` here, which meant a tab switch unmounted them:
  // adjust three scores, glance at the Timeline, come back, and the buffer was
  // empty with nothing having said so (#1247). DemandDetail holds them now, so
  // leaving and returning to this tab finds the edits still there.
  //
  // This one does NOT commit on leaving, unlike the inline fields in the same
  // issue. A tab switch is not a blur, and the Save bar exists because a
  // priority change is a council decision — silently writing one because
  // somebody looked at another tab would be worse than the loss it replaces.
  const { tier: pendingTier, scores: pendingScores } = draft
  const setPendingTier = (tier: string | null) => onDraftChange({ ...draft, tier })
  const setPendingScores = (scores: Record<string, number>) =>
    onDraftChange({ ...draft, scores })
  const [saving, setSaving] = useState(false)
  // Stage-coupling confirmation
  // Flash highlight on the reason field when the tier changes so the
  // reviewer is nudged to record a rationale before saving.
  const [reasonHighlightKey, setReasonHighlightKey] = useState(0)
  const reasonRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (pendingTier !== null) setReasonHighlightKey((k) => k + 1)
  }, [pendingTier])

  const hasPendingChanges = pendingTier !== null || Object.keys(pendingScores).length > 0
  const displayTier = pendingTier ?? activeTier
  // Preserved through parking rather than cleared, so it can be stale on the
  // way back out — show how stale instead of throwing it away.
  const priorityAgeMonths = monthsSince(demand.priority_override_at)
  const displayScores: Record<CriterionDef['key'], number | null> = {
    strategic_importance: pendingScores.strategic_importance ?? scores.strategic_importance,
    competitiveness: pendingScores.competitiveness ?? scores.competitiveness,
    readiness: pendingScores.readiness ?? scores.readiness,
    feasibility: pendingScores.feasibility ?? scores.feasibility,
  }

  function pickQuadrant(key: QuadrantDef['key']) {
    if (!canEdit) return
    // Click the same active tier → clear the pending change (back to persisted).
    if (key === activeTier) {
      setPendingTier(null)
      return
    }
    // Parking Lot is derived from the stage, so it is not a bucket you pick.
    // The old code fired a stage transition from here — a second, untransacted
    // request after the PATCH had already committed, which could leave the two
    // out of step. Move the demand's stage instead and this follows.
    if (key === 'parking-lot') return
    // While the demand is parked the bucket underneath is preserved but not
    // in charge — editing it would be invisible until the demand resumes.
    if (derivedTier) return
    setPendingTier(key)
  }

  function adjustScore(key: CriterionDef['key'], delta: number) {
    if (!canEdit) return
    const current = displayScores[key] ?? 0
    const next = roundStep(Math.max(MIN, Math.min(MAX, current + delta)))
    if (next === scores[key]) {
      const remaining = { ...pendingScores }
      delete remaining[key]
      setPendingScores(remaining)
    } else {
      setPendingScores({ ...pendingScores, [key]: next })
    }
  }

  async function confirmChanges() {
    const patch: Record<string, string | number> = { ...pendingScores }
    if (pendingTier !== null) patch.priority_tier_override = pendingTier
    if (Object.keys(patch).length === 0 || saving) return
    setSaving(true)
    try {
      await onSaveMany(patch)
      onDraftChange(EMPTY_PRIORITY_DRAFT)
    } catch {
      // The parent toasted; the buffer stays so the user retries, not retypes.
    } finally {
      setSaving(false)
    }
  }

  function discardChanges() {
    onDraftChange(EMPTY_PRIORITY_DRAFT)
  }

  return (
    <div className={styles.root} data-testid="priority-tab">
      {hasPendingChanges && (
        <div className={styles.saveBar}>
          <span className={styles.saveBarText}>You have unsaved priority changes</span>
          <Button appearance="primary" size="small" onClick={confirmChanges} disabled={saving}>
            Save
          </Button>
          <Button appearance="secondary" size="small" onClick={discardChanges} disabled={saving}>
            Cancel
          </Button>
        </div>
      )}

      {/* ── Priority bucket ─────────────────────────────────────────── */}
      <section className={styles.section}>
        <h3 className={styles.title}>Priority bucket</h3>
        {/* The caption used to say "Click a quadrant to set the priority
            bucket" even when every tile was inert — on a rejected demand with
            no stored bucket, that instruction was the only thing on screen and
            following it did nothing. It now says what is actually true. */}
        <Caption1 style={{ display: 'block', marginBottom: 12 }} data-testid="priority-hint">
          {!canEdit
            ? 'The council for this focus area sets the priority bucket.'
            : derivedTier
              ? `This demand's bucket follows its ${demand.closure_type === 'rejected' ? 'closure' : 'stage'}, so it is not picked here.`
              : "Pick which quadrant this demand sits in. Council's call — the rationale below lives in the audit trail."}
        </Caption1>
        <div className={styles.quadrantGrid} role="radiogroup" aria-label="Priority quadrant">
          {QUADRANTS.map((q) => {
            const isActive = q.key === displayTier
            const hoverCls = styles[q.hoverKey]
            const activeCls = isActive ? styles[q.activeKey] : ''
            // The same three conditions `pickQuadrant` bails on, stated once so
            // the tile's appearance and its behaviour cannot drift apart. They
            // did: every one of these was styled, hoverable and enabled while
            // the handler returned immediately.
            const pickable = canEdit && !derivedTier && q.key !== 'parking-lot'
            return (
              <button
                key={q.key}
                type="button"
                role="radio"
                aria-checked={isActive}
                disabled={!pickable}
                onClick={() => pickQuadrant(q.key)}
                className={`${styles.quadrant} ${hoverCls} ${activeCls} ${pickable ? '' : styles.quadrantInert}`.trim()}
                data-testid={`quadrant-${q.key}`}
                data-priority={q.key}
              >
                <span className={styles.quadrantTitle}>{q.title}</span>
                {isActive && (
                  <span
                    style={{
                      position: 'absolute',
                      top: 6,
                      right: 8,
                      fontWeight: 700,
                      fontSize: 16,
                      opacity: 1,
                      textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    }}
                  >
                    ✓ Selected
                  </span>
                )}
              </button>
            )
          })}
        </div>
        {displayTier ? (
          <div className={styles.quadrantSummary} data-testid="priority-summary">
            {derivedTier ? 'Derived: ' : 'Picked: '}
            <strong>{QUADRANTS.find((q) => q.key === displayTier)?.title}</strong>
            {derivedTier && (
              <span className={styles.derivedNote}>
                {' '}
                — follows the demand&rsquo;s{' '}
                {demand.closure_type === 'rejected' ? 'closure' : 'stage'}, not chosen here
              </span>
            )}
            {priorityAgeMonths !== null && !derivedTier && (
              <span
                className={priorityAgeMonths >= STALE_AFTER_MONTHS ? styles.stale : styles.age}
                data-testid="priority-age"
              >
                {' '}
                — set{' '}
                {priorityAgeMonths === 0
                  ? 'this month'
                  : `${priorityAgeMonths} month${priorityAgeMonths === 1 ? '' : 's'} ago`}
              </span>
            )}
          </div>
        ) : (
          <div className={styles.quadrantSummary}>
            Click a quadrant to set the priority bucket.
          </div>
        )}
        <div className={styles.kvRow}>
          <span className={styles.kvKey}>Reason</span>
          <div
            key={reasonHighlightKey}
            ref={reasonRef}
            className={pendingTier !== null ? styles.reasonHighlight : undefined}
          >
            <EditableField
              value={demand.priority_override_reason ?? ''}
              path="priority_override_reason"
              onSave={onSave}
              disabled={!canEdit}
              multiline
              placeholder="Why this bucket?"
              ariaLabel="Priority reason"
            />
          </div>
        </div>
        {(demand.priority_override_by || demand.priority_override_at) && (
          <Caption1
            data-testid="priority-assigned-by"
            style={{ display: 'block', marginTop: 8 }}
          >
            Assigned by{' '}
            {demand.priority_override_by ? (
              <strong>user #{demand.priority_override_by}</strong>
            ) : (
              <strong>council</strong>
            )}
            {demand.priority_override_at && (
              <> on {new Date(demand.priority_override_at).toLocaleDateString()}</>
            )}
          </Caption1>
        )}
      </section>

      {/* ── Detailed scoring ────────────────────────────────────────── */}
      <section className={styles.section}>
        <h3 className={styles.title}>Detailed scoring (FA criteria)</h3>
        <Caption1 style={{ display: 'block', marginBottom: 12 }}>
          Score each criterion from 0.0 to 1.0 in 0.1 steps. Notes are optional but recommended
          for the audit trail.
        </Caption1>
        {CRITERIA.map((c) => {
          const current = displayScores[c.key]
          const reasonKey = `${c.key}_reason` as const
          const valueStyle = current == null ? styles.scoreValueUnset : undefined
          const valueBg = gradientBackground(current)
          return (
            <div key={c.key} className={styles.criterion} data-testid={`criterion-${c.key}`}>
              <div className={styles.criterionHead}>
                <span>
                  <strong style={{ fontSize: 13 }}>{c.label}</strong>
                </span>
                <div className={styles.scoreControl}>
                  <Button
                    appearance="secondary"
                    size="small"
                    className={styles.scoreButton}
                    icon={<Subtract16Regular />}
                    aria-label={`Decrease ${c.label}`}
                    onClick={() => adjustScore(c.key, -STEP)}
                    disabled={!canEdit || (current != null && current <= MIN)}
                    data-testid={`score-${c.key}-minus`}
                  />
                  <span
                    className={`${styles.scoreValue} ${valueStyle ?? ''}`.trim()}
                    style={valueBg ? { background: valueBg } : undefined}
                    aria-label={`${c.label} score`}
                    data-testid={`score-${c.key}-value`}
                  >
                    {formatScore(current)}
                  </span>
                  <Button
                    appearance="secondary"
                    size="small"
                    className={styles.scoreButton}
                    icon={<Add16Regular />}
                    aria-label={`Increase ${c.label}`}
                    onClick={() => adjustScore(c.key, STEP)}
                    disabled={!canEdit || (current != null && current >= MAX)}
                    data-testid={`score-${c.key}-plus`}
                  />
                </div>
              </div>
              <div style={{ marginTop: 4, paddingLeft: 2 }}>
                <EditableField
                  value={
                    ((demand as unknown as Record<string, unknown>)[reasonKey] as string) ?? ''
                  }
                  path={reasonKey}
                  onSave={onSave}
                  disabled={!canEdit}
                  multiline
                  placeholder={`Why this ${c.label.toLowerCase()} score?`}
                  ariaLabel={`${c.label} reason`}
                />
              </div>
            </div>
          )
        })}
      </section>

    </div>
  )
}
